import * as Sentry from '@sentry/react';
import { apiUrl } from './api-base';
import type { FeatureAccessSnapshot, Member } from '../types';

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    tunnel: apiUrl('/api/sentry-tunnel'),
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'development',
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    sendDefaultPii: true,
  });
}

export function syncSentryMemberContext(
  member: Member | null,
  options?: {
    isSuperAdmin?: boolean;
    featureAccess?: FeatureAccessSnapshot | null;
  },
) {
  if (!member) {
    Sentry.setUser(null);
    Sentry.setTag('member_role', 'anonymous');
    Sentry.setTag('is_admin', false);
    Sentry.setTag('is_super_admin', false);
    Sentry.setContext('auth', null);
    return;
  }

  Sentry.setUser({
    id: member.uid,
    email: member.email,
    username: member.name,
  });
  Sentry.setTag('member_role', member.role);
  Sentry.setTag('is_admin', member.isAdmin);
  Sentry.setTag('is_super_admin', options?.isSuperAdmin === true);
  Sentry.setContext('auth', {
    uid: member.uid,
    role: member.role,
    isAdmin: member.isAdmin,
    isSuperAdmin: options?.isSuperAdmin === true,
    hasPhone: Boolean(member.phone?.trim()),
    hasLmNumber: Boolean(member.lmNumber?.trim()),
    featureAccess: options?.featureAccess ?? null,
  });
}

export async function captureSentryTestError(source: string): Promise<string | undefined> {
  const eventId = Sentry.captureException(new Error(`sentry-spa-test: ${source}`));
  await Sentry.flush(5000);
  return eventId;
}
